import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { clampValue, type ProgressState, resolveProgressState } from '../../utils';

const DEFAULT_SIZE = 18;
const DEFAULT_SPINNER_SIZE = 24;
const DEFAULT_STROKE_WIDTH = 2.5;

const spinnerVariants = cva('block shrink-0', {
  variants: {
    tone: {
      brand: 'text-orange-50',
      inverse: 'text-white',
      neutral: 'text-grey-90',
    },
  },
  defaultVariants: {
    tone: 'brand',
  },
});

type CircularProgressProps = {
  value?: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  className?: string;
  ariaLabel?: string;
  /** Renders a spinning loading indicator in a `role="status"` region instead of a progress ring; `className` and `data-testid` go on that region, other props on the svg. */
  indeterminate?: boolean;
  'data-testid'?: string;
} & VariantProps<typeof spinnerVariants> &
  Omit<React.ComponentProps<'svg'>, 'aria-label' | 'children' | 'viewBox'>;

const COLOR_MAP: Record<ProgressState, string> = {
  'in-progress': 'var(--color-blue-40)',
  complete: 'var(--color-green-50)',
  pending: 'var(--color-grey-20)',
};

function CircularProgressComponent(
  {
    value,
    size,
    strokeWidth,
    trackColor,
    className,
    ariaLabel,
    indeterminate = false,
    tone,
    'data-testid': testId,
    ...props
  }: CircularProgressProps,
  ref: React.Ref<SVGSVGElement>,
) {
  if (indeterminate) {
    const spinnerSize = size ?? DEFAULT_SPINNER_SIZE;
    const spinnerStroke = strokeWidth ?? spinnerSize / 6;
    const radius = (spinnerSize - spinnerStroke) / 2;
    const circumference = 2 * Math.PI * radius;

    return (
      // biome-ignore lint/a11y/useSemanticElements: <output> is for form results, not a loading indicator
      <span
        role="status"
        data-slot="circular-progress"
        data-testid={testId}
        className={cn(spinnerVariants({ tone }), className)}
      >
        <svg
          ref={ref}
          className="motion-safe:animate-spin"
          width={spinnerSize}
          height={spinnerSize}
          viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
          fill="none"
          {...props}
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx={spinnerSize / 2}
            cy={spinnerSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={spinnerStroke}
          />
          <circle
            className="opacity-75"
            cx={spinnerSize / 2}
            cy={spinnerSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={spinnerStroke}
            strokeLinecap="round"
            strokeDasharray={`${circumference / 4} ${circumference}`}
          />
        </svg>
        <span className="sr-only">{ariaLabel ?? 'Loading'}</span>
      </span>
    );
  }

  const ringSize = size ?? DEFAULT_SIZE;
  const ringStroke = strokeWidth ?? DEFAULT_STROKE_WIDTH;
  const resolvedState = resolveProgressState(value);
  const clampedValue = resolvedState === 'pending' ? 100 : clampValue(value);
  const radius = (ringSize - ringStroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = resolvedState === 'pending' ? 0 : circumference - (clampedValue / 100) * circumference;
  const progressColor = resolvedState === 'pending' ? (trackColor ?? COLOR_MAP.pending) : COLOR_MAP[resolvedState];

  const generatedAriaLabel =
    resolvedState === 'pending'
      ? 'Pending'
      : `${Math.round(clampedValue)}% ${resolvedState === 'complete' ? 'Complete' : 'Complete'}`;

  return (
    <svg
      ref={ref}
      role="img"
      data-testid={testId}
      className={className}
      width={ringSize}
      height={ringSize}
      viewBox={`0 0 ${ringSize} ${ringSize}`}
      fill="none"
      aria-label={ariaLabel ?? generatedAriaLabel}
      {...props}
    >
      <circle
        cx={ringSize / 2}
        cy={ringSize / 2}
        r={radius}
        stroke={progressColor}
        strokeWidth={ringStroke}
        strokeLinecap="round"
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
      />
    </svg>
  );
}

const CircularProgress = React.forwardRef(CircularProgressComponent);

export { CircularProgress, type CircularProgressProps };
